import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
    TrendingUp, RefreshCw, Target, Wallet, MapPin, Home, Flag, Calculator,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { fetchInvestorDemandIndex, fetchInvestorDemandIndexList } from '../api/analyticsApi';
import EngagementListModal from './EngagementListModal';

const fmtMoney = (v) => {
    if (v === null || v === undefined) return '—';
    if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
    if (v >= 1e3) return `$${Math.round(v / 1e3)}k`;
    return `$${Math.round(v).toLocaleString()}`;
};
const fmtPct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

// Headline metric tile
const MetricTile = ({ icon: Icon, label, value, sub, color }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-gray-400 text-xs mb-1">
            <Icon className="w-4 h-4" style={{ color }} /> {label}
        </div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        {sub != null && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
);

// Horizontal "buyers want this" ranking bar list. Every row drills into the
// investors behind it when it carries a `segment_key` (Christian, Monday
// 12759048834: "make these buttons clickable to see which buyers interested").
const RankBars = ({ items, labelFor, valueFor, color, emptyText, onItem }) => {
    const max = Math.max(1, ...items.map(valueFor));
    if (!items.length) {
        return <p className="text-xs text-gray-400 py-6 text-center">{emptyText}</p>;
    }
    return (
        <div className="space-y-2.5">
            {items.map((item, idx) => {
                const value = valueFor(item);
                const clickable = !!onItem && !!item.segment_key && value > 0;
                return (
                    <button
                        key={idx}
                        type="button"
                        onClick={clickable ? () => onItem(item) : undefined}
                        disabled={!clickable}
                        title={clickable
                            ? `View the ${value} investor${value === 1 ? '' : 's'} behind ${labelFor(item)}`
                            : undefined}
                        className={`group w-full flex items-center gap-3 text-left rounded-md px-1 -mx-1 py-0.5 transition-colors ${
                            clickable ? 'cursor-pointer hover:bg-gray-50' : 'cursor-default'
                        }`}
                    >
                        <span className="text-xs text-gray-400 w-5 flex-shrink-0 text-right">{idx + 1}</span>
                        <span className="text-xs text-gray-700 w-36 flex-shrink-0 truncate group-hover:text-gray-900"
                              title={labelFor(item)}>
                            {labelFor(item)}
                        </span>
                        <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                            <div className="h-4 rounded-full transition-all group-hover:opacity-80"
                                 style={{ width: `${Math.max(6, (value / max) * 100)}%`, background: color }} />
                        </div>
                        <span className="text-xs font-semibold text-gray-900 w-16 text-right">
                            {value.toLocaleString()} {value === 1 ? 'buyer' : 'buyers'}
                        </span>
                    </button>
                );
            })}
        </div>
    );
};

const Section = ({ title, icon: Icon, accent, children }) => (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-900 mb-1 flex items-center gap-2">
            <Icon className="w-4 h-4" style={{ color: accent }} /> {title}
        </h3>
        {children}
    </div>
);

/**
 * Investor Demand Index — "What Investors Are Actually Looking For".
 * Investor-segmented sentiment from declared buying preferences: average target
 * yield & budget, most-searched suburbs / asset types / strategies, plus the
 * cohort's real investment-calculator benchmark.
 */
const InvestorDemandIndexPanel = ({ startDate, endDate }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const tokenRef = useRef(0);
    const [listModal, setListModal] = useState({ open: false, loading: false, title: '', subtitle: '', rows: [] });

    const openSegment = useCallback(async (segment, title, count) => {
        setListModal({
            open: true, loading: true, title,
            subtitle: `${count} investor${count === 1 ? '' : 's'}`, rows: [],
        });
        try {
            const res = await fetchInvestorDemandIndexList(startDate, endDate, segment);
            setListModal((m) => ({ ...m, loading: false, rows: res?.rows || [] }));
        } catch (err) {
            console.error('Investor demand drill-down failed:', err);
            toast.error('Failed to load the buyer list');
            setListModal((m) => ({ ...m, loading: false, rows: [] }));
        }
    }, [startDate, endDate]);

    useEffect(() => {
        const myToken = ++tokenRef.current;
        setLoading(true);
        fetchInvestorDemandIndex(startDate, endDate)
            .then((res) => { if (myToken === tokenRef.current) setData(res); })
            .catch((err) => {
                if (myToken !== tokenRef.current) return;
                console.error('Investor demand index load failed:', err);
                toast.error('Failed to load investor demand index');
            })
            .finally(() => { if (myToken === tokenRef.current) setLoading(false); });
    }, [startDate, endDate]);

    if (loading && !data) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-500">
                <RefreshCw className="w-5 h-5 animate-spin mr-2" /> Loading investor demand…
            </div>
        );
    }
    if (!data) return null;

    const ty = data.target_yield || {};
    const budget = data.budget || {};
    const bench = data.modelled_benchmark || {};

    return (
        <div className="space-y-6">
            <div>
                <h2 className="text-xl font-bold text-gray-900 mb-1 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-emerald-600" /> What Investors Are Actually Looking For
                </h2>
                <p className="text-sm text-gray-500">
                    Investor sentiment from {(data.cohort_size ?? 0).toLocaleString()} investor{data.cohort_size === 1 ? '' : 's'} —
                    what they told us they want. Every figure below is a declared buying preference.
                </p>
            </div>

            {/* Headline: target yield + budget */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <MetricTile
                    icon={Target} label="Avg target yield" color="#10b981"
                    value={fmtPct(ty.avg)}
                    sub={ty.median != null ? `median ${ty.median}% · ${ty.sample} stated` : `${ty.sample || 0} stated`}
                />
                <MetricTile
                    icon={Wallet} label="Avg budget" color="#6366f1"
                    value={fmtMoney(budget.avg)}
                    sub={budget.median != null ? `median ${fmtMoney(budget.median)} · ${budget.sample} stated` : `${budget.sample || 0} stated`}
                />
                <MetricTile
                    icon={Calculator} label="Modelling yield (gross)" color="#f59e0b"
                    value={fmtPct(bench.avg_gross_yield_pct)}
                    sub={bench.sample_size ? `${bench.sample_size} calc runs` : 'no calc runs'}
                />
                <MetricTile
                    icon={Calculator} label="Modelling price" color="#f59e0b"
                    value={fmtMoney(bench.avg_price)}
                    sub={bench.avg_monthly_cash_flow != null ? `${fmtMoney(bench.avg_monthly_cash_flow)}/mo cashflow` : null}
                />
            </div>

            {/* Rankings */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
                <Section title="Most-searched suburbs" icon={MapPin} accent="#6366f1">
                    <RankBars
                        items={data.most_searched_suburbs || []}
                        labelFor={(i) => i.suburb}
                        valueFor={(i) => i.buyers || 0}
                        color="#6366f1"
                        emptyText="No suburbs declared yet."
                        onItem={(i) => openSegment(i.segment_key, `Investors targeting ${i.suburb}`, i.buyers || 0)}
                    />
                </Section>
                <Section title="Most-searched asset types" icon={Home} accent="#0891b2">
                    <RankBars
                        items={data.most_searched_asset_types || []}
                        labelFor={(i) => i.type}
                        valueFor={(i) => i.buyers || 0}
                        color="#0891b2"
                        emptyText="No asset types declared yet."
                        onItem={(i) => openSegment(i.segment_key, `Investors wanting ${i.type}`, i.buyers || 0)}
                    />
                </Section>
                <Section title="Most-searched strategies" icon={Flag} accent="#e11d48">
                    <RankBars
                        items={data.most_searched_strategies || []}
                        labelFor={(i) => i.label}
                        valueFor={(i) => i.buyers || 0}
                        color="#e11d48"
                        emptyText="No investment strategies declared yet."
                        onItem={(i) => openSegment(i.segment_key, `Investors chasing ${i.label}`, i.buyers || 0)}
                    />
                </Section>
            </div>

            <p className="text-[11px] text-gray-400">
                Click any ranking row to see exactly which investors are behind it. Investor cohort = portal
                buyers whose buying preferences flag them as an investor/developer or who set any investment
                strategy. Yield &amp; budget are self-reported preferences; the modelling figures are the same
                cohort's actual investment-calculator runs.
            </p>

            <EngagementListModal
                isOpen={listModal.open}
                onClose={() => setListModal((m) => ({ ...m, open: false }))}
                title={listModal.title}
                subtitle={listModal.subtitle}
                loading={listModal.loading}
                rows={listModal.rows}
                variant="buyers"
            />
        </div>
    );
};

export default InvestorDemandIndexPanel;
